// src/components/Common/ColorsManager.js
import React, { useState, useEffect } from 'react';
import { supabase } from '../../services/supabaseClient';
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  Divider
} from '@mui/material';
import { 
  Delete as DeleteIcon, 
  Edit as EditIcon,
  Close as CloseIcon  // ADDED: Import Close icon
} from '@mui/icons-material';

const ColorsManager = ({ open, onClose }) => {
  const [colors, setColors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editColor, setEditColor] = useState(null);
  const [newColorName, setNewColorName] = useState('');

  const rtlTextFieldSx = {
    '& .MuiOutlinedInput-root': {
      '& .MuiOutlinedInput-notchedOutline legend': {
        textAlign: 'right',
        transformOrigin: 'top right',
        right: 12,
        left: 'auto'
      }
    },
    '& .MuiInputBase-input': {
      textAlign: 'right',
      direction: 'rtl',
      fontWeight: 'bold'
    }
  };

  useEffect(() => {
    if (open) fetchColors();
  }, [open]);

  const fetchColors = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('colors')
        .select('*')
        .order('name', { ascending: true });

      if (error) throw error;
      setColors(data || []);
    } catch (error) {
      console.error('Failed to fetch colors:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddColor = async () => {
    if (!newColorName.trim()) return;

    try {
      const { data, error } = await supabase
        .from('colors')
        .insert([{ name: newColorName.trim() }])
        .select()
        .single();

      if (error) throw error;
      setColors([...colors, data]);
      setNewColorName('');
    } catch (error) {
      console.error('Failed to add color:', error);
    }
  };

  const handleUpdateColor = async () => {
    if (!editColor || !editColor.name.trim()) return;

    try {
      const { error } = await supabase
        .from('colors')
        .update({ name: editColor.name.trim() })
        .eq('id', editColor.id);

      if (error) throw error;

      setColors(colors.map(color =>
        color.id === editColor.id ? { ...color, name: editColor.name } : color
      ));
      setEditColor(null);
    } catch (error) {
      console.error('Failed to update color:', error);
    }
  };

  const handleDeleteColor = async (color) => {
    if (!window.confirm(`هل أنت متأكد من حذف "${color.name}"؟`)) return;

    try {
      const { error } = await supabase
        .from('colors')
        .delete()
        .eq('id', color.id);

      if (error) throw error;
      setColors(colors.filter(c => c.id !== color.id));
    } catch (error) {
      console.error('Failed to delete color:', error);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      {/* UPDATED: Added close icon to top-left */}
      <DialogTitle sx={{ 
        fontWeight: 'bold', 
        textAlign: 'right', 
        direction: 'rtl',
        position: 'relative',
        py: 2,
        pr: 3
      }}>
        إدارة الألوان
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 10,
            left: 22,  // Changed from right to left
            color: '#0f3662',
            padding: '0.2rem',
            borderRadius: '25%', 
            border: '2px solid #a72c2a'
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ mb: 2 }}>
            <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" sx={{ 
                fontWeight: 'bold', 
                textAlign: 'right',
                mb: 3
              }}>
                الألوان
              </Typography>
              
              {/* Add Color Section */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'flex-end', 
                mb: 2,
                gap: 1
              }}>
                <Button 
                  variant="contained" 
                  onClick={handleAddColor}
                  sx={{ fontWeight: 'bold' }}
                >
                  إضافة
                </Button>
                <TextField
                  fullWidth
                  placeholder="اسم اللون الجديد"
                  value={newColorName}
                  onChange={(e) => setNewColorName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddColor()}
                  sx={rtlTextFieldSx}
                />
              </Box>

              {/* Colors List */}
              <List component={Paper} variant="outlined" sx={{ p: 0 }}>
                {colors.length > 0 ? (
                  colors.map((color) => (
                    <React.Fragment key={color.id}>
                      <ListItem>
                        <ListItemText 
                          primary={color.name} 
                          primaryTypographyProps={{ fontWeight: 'bold', textAlign: 'right' }}
                        />
                        <ListItemSecondaryAction sx={{
                          display: 'flex',
                          gap: '8px',
                          left: 16,
                          right: 'auto'
                        }}>
                          <IconButton onClick={() => handleDeleteColor(color)}>
                            <DeleteIcon color="error" />
                          </IconButton>
                          <IconButton onClick={() => setEditColor({ ...color })}>
                            <EditIcon color="primary" />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                      <Divider />
                    </React.Fragment>
                  ))
                ) : (
                  <ListItem>
                    <ListItemText 
                      primary="لا توجد ألوان مضافة" 
                      primaryTypographyProps={{ fontWeight: 'bold', textAlign: 'right' }}
                    />
                  </ListItem>
                )}
              </List>
            </Paper>
          </Box>
        )}
      </DialogContent>

      {/* Edit Dialog */}
      <Dialog
        open={!!editColor}
        onClose={() => setEditColor(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold', textAlign: 'right', direction: 'rtl' }}>
          تعديل اللون
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="اسم اللون"
            value={editColor?.name || ''}
            onChange={(e) => setEditColor({ ...editColor, name: e.target.value })}
            InputLabelProps={{
              sx: {
                fontWeight: 'bold',
                textAlign: 'right',
                transformOrigin: 'top right',
                right: 26,
                left: 'auto'
              }
            }}
            sx={rtlTextFieldSx}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditColor(null)} sx={{ fontWeight: 'bold' }}>إلغاء</Button>
          <Button onClick={handleUpdateColor} color="primary" sx={{ fontWeight: 'bold' }}>
            حفظ التغييرات
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default ColorsManager;